
import { defineComponent } from 'vue';

import { checkHasAccountAtMep, createEmployee } from '@/services/yourdata.service';

import { FormGroup } from 'mq-ui-kit/lib/FormGroup';
import { FormRow } from 'mq-ui-kit/lib/FormRow';
import { FormColumn } from 'mq-ui-kit/lib/FormColumn';
import { FormLabel } from 'mq-ui-kit/lib/FormLabel';
import { TextInput } from 'mq-ui-kit/lib/TextInput';
import { CheckBox } from 'mq-ui-kit/lib/CheckBox';
import { Button } from 'mq-ui-kit/lib/Button';
import { Confirmation } from 'mq-ui-kit/lib/Confirmation';

import ToastService from 'mq-ui-kit/lib/toast';
import { FormGroupModel } from 'mq-ui-kit/lib/form-group-model';
import { Validators } from 'mq-ui-kit/lib/form-validate';
import { FormControlModel } from 'mq-ui-kit/lib/form-control-model';

import * as Actions from '@/store/actions';
import * as Constants from '@/constants';
import campaignService from '@/services/campaign.service';

export default defineComponent({
  name: 'YourDataForm',

  components: {
    FormRow,
    FormColumn,
    FormLabel,
    TextInput,
    FormGroup,
    CheckBox,
    Button,
    Confirmation,
  },

  data() {
    return {
      utmCampaign: '',
      disableButton: false,
      showLoading: false,
      confirmationIsOpen: false,
      campaign: Constants.MEP_STRING,

      formGroup: new FormGroupModel({
        name: new FormControlModel({
          validations: [Validators.required(), Validators.maxCharacter(100)],
          value: '',
          disabled: false,
          messages: {
            required: 'yourData.requiredField',
            maxLength: 'yourData.nameMaxLength',
          },
        }),
        emailAddress: new FormControlModel({
          validations: [Validators.required(), Validators.email(), Validators.maxCharacter(50)],
          value: '',
          disabled: false,
          messages: {
            required: 'yourData.requiredField',
            email: 'yourData.emailValidation',
            maxLength: 'yourData.emailAddressMaxLength',
          },
        }),
        countryCode: new FormControlModel({
          validations: [Validators.required()],
          value: Constants.NETHERLANDS_COUNTRY_CODE,
          disabled: true,
          messages: {
            required: 'yourData.requiredField',
          },
        }),
        telephoneNumber: new FormControlModel({
          validations: [
            Validators.required(),
            Validators.phoneNumber(),
            Validators.maxCharacter(9),
          ],
          value: '',
          disabled: false,
          messages: {
            required: 'yourData.requiredField',
            phone: 'yourData.phoneNumberValidation',
          },
        }),
        age: new FormControlModel({
          validations: [Validators.required(), Validators.min(13), Validators.max(100)],
          value: '',
          disabled: false,
          messages: {
            required: 'yourData.requiredField',
            min: 'yourData.ageMinValidation',
            max: 'yourData.ageMaxValidation',
          },
        }),
        postCode: new FormControlModel({
          validations: [Validators.required(),
            Validators.maxCharacter(7),
            Validators.minCharacter(6),
            Validators.zipCode()],
          value: '',
          disabled: false,
          messages: {
            required: 'yourData.requiredField',
            maxLength: 'yourData.postCodeMaxLength',
            minLength: 'yourData.postCodeMinLength',
            zipCode: 'yourData.zipCodeValidationForDutch',
          },
        }),
        city: new FormControlModel({
          validations: [Validators.required(),
            Validators.maxCharacter(50),
            Validators.minCharacter(2)],
          value: '',
          disabled: false,
          messages: {
            required: 'yourData.requiredField',
            maxLength: 'yourData.cityMaxLength',
            minLength: 'yourData.cityMinLength',
          },
        }),
        isChecked: new FormControlModel({
          validations: [Validators.required()],
          value: false,
          disabled: false,
          messages: {
            required: 'yourData.checkboxValidation',
          },
        }),
      }),
    };
  },

  computed: {
    allComponentsFilled(): boolean {
      return this.formGroup.valid;
    },
  },

  created() {
    this.checkUTMParameters();

    if (this.utmCampaign && !this.$store.state.campaign) {
      this.getCampaigns(this.utmCampaign);
    } else if (!this.utmCampaign && !this.$store.state.campaign) {
      const promise = campaignService.getDefaultCampaign();
      promise.then((campaign) => {
        this.$store.dispatch(Actions.SET_SELECTED_CAMPAIGN, campaign);
      })
        .catch(() => {
          ToastService.errorToast(this, this.$t('chooseProject.errorCampaignListLoad'));
        });
    }
  },

  mounted() {
    if (this.$store.state.yourdata) {
      this.formGroup.reset({
        name: this.$store.state.yourdata.name,
        emailAddress: this.$store.state.yourdata.emailAddress,
        countryCode: this.$store.state.yourdata.countryCode,
        telephoneNumber: this.$store.state.yourdata.telephoneNumber,
        age: this.$store.state.yourdata.age,
        postCode: this.$store.state.yourdata.postCode,
        city: this.$store.state.yourdata.city,
        isChecked: this.$store.state.yourdata.isChecked,
      });
    }
  },

  methods: {
    sanitizeInput() {
      this.formGroup.controls.telephoneNumber.setValue((this.formGroup.controls.telephoneNumber.value as string).replace(/\D/g, ''));
    },
    checkUTMParameters() {
      const params = window.location.search.toString()
        .slice(1)
        .split('&');

      if (params) {
        params.forEach((item) => {
          const [utm, value] = item.split('=');
          if (utm === 'utm_campaign') {
            this.utmCampaign = value;
          }
        });
      }
    },

    clickHandlerForConfirm() {
      this.confirmationIsOpen = false;
    },

    openConfirmationBox() {
      this.confirmationIsOpen = true;
    },

    goToChooseProjectPage() {
      this.$router.push({
        name: 'ChooseProject',
      });
    },

    goToAppointmentPage() {
      this.$router.push({
        name: 'Appointment',
      });
    },

    goToBackPage() {
      this.$router.go(-1);
    },

    checkWhetherAccountExistsForEmailAddress() {
      const email = this.formGroup.controls.emailAddress.getValue();
      checkHasAccountAtMep(email as string)
        .then((result) => {
          this.$store.dispatch(Actions.SET_HAS_ACCOUNT_AT_MEP_FLAG, result);
        })
        .catch(() => {
          ToastService.errorToast(this, this.$t('yourData.errorCallingHasAccountAtMepAPI'));
        });
    },

    saveYourData() {
      this.disableButton = true;
      this.showLoading = true;
      this.checkWhetherAccountExistsForEmailAddress();

      this.$store.dispatch(Actions.SET_YOURDATA, this.formGroup.getValue())
        .then(() => {
          if (this.$store.state.yourdata) {
            const data = this.$store.state.yourdata;
            if (this.$store.state.campaign) {
              this.campaign = this.$store.state.campaign.campaignCode || Constants.MEP_STRING;
            }

            createEmployee({
              name: data.name,
              emailAddress: data.emailAddress,
              countryCode: data.countryCode,
              telephoneNumber: data.telephoneNumber,
              age: Number(data.age),
              postCode: data.postCode,
              city: data.city,
              isChecked: true,
            }, this.campaign)
              .then((createEmployeeResponse) => {
                this.$store.dispatch(Actions.SET_TOKEN, createEmployeeResponse.token);
                if (createEmployeeResponse.employeeId) {
                  this.$store.dispatch(Actions.SET_EMPLOYEEID, createEmployeeResponse.employeeId);
                  this.goToAppointmentPage();
                }
              })
              .catch(() => {
                ToastService.errorToast(this, this.$t('yourData.errorCallingCreateEmployeeAPI'));
                this.disableButton = false;
                this.showLoading = false;
              });
          }
        })
        .catch(() => {
          ToastService.errorToast(this, this.$t('yourData.errorStoringData'));
          this.disableButton = false;
          this.showLoading = false;
        });
    },

    getCampaigns(value: string) {
      const promise = campaignService.getCampaignForUtmCampaign(value);
      promise.then((campaign) => {
        if (value != null && campaign == null) {
          this.goToChooseProjectPage();
        } else if (value != null && campaign != null) {
          this.$store.dispatch(Actions.SET_SELECTED_CAMPAIGN, campaign);
        }
      });
    },
  },
});
